import { z } from "zod";

/**
 * WebSocket messages on /link/ws (§17.2). Masks are per-step; `frame` is the link-local step index.
 * JSON text frames carry every `LinkMessage`; bulk payloads travel as binary frames (see `LinkBinOp`).
 */
export const LinkMessage = z.discriminatedUnion("t", [
  z.object({ t: z.literal("hello"), lobbyId: z.string(), playerId: z.string(), sessionId: z.string() }),
  z.object({ t: z.literal("ready"), side: z.enum(["A", "B"]), inputDelay: z.number().int(), linkSessionId: z.string() }),
  z.object({ t: z.literal("mask"), frame: z.number().int(), mask: z.number().int().min(0).max(255) }),
  z.object({ t: z.literal("peerMask"), frame: z.number().int(), mask: z.number().int().min(0).max(255) }),
  z.object({ t: z.literal("hash"), frame: z.number().int(), stateHash: z.string() }),
  z.object({ t: z.literal("desync"), frame: z.number().int() }),
  // stepsBoth: steps both cores ran (authoritative LINK_END = LINK_BEGIN + stepsBoth); absent from server-run.
  z.object({ t: z.literal("end"), reason: z.enum(["done", "peer_left", "desync", "timeout"]), stepsBoth: z.number().int().nonnegative().optional() }),
  z.object({ t: z.literal("ping"), at: z.number() }),
  z.object({ t: z.literal("pong"), at: z.number() }),
  /**
   * S→C, lockstep only: announces the next binary frame, `0x01 STATE`, holding the opponent's verified
   * Cable Club core state (`side` is whose state it is; `bytes` its zstd size, `sha256` hex of the
   * decompressed state). Load it only into a link core.
   */
  z.object({ t: z.literal("state"), side: z.enum(["A", "B"]), bytes: z.number().int().nonnegative(), sha256: z.string() }),
  /** S→C, server-run only: the audio stream that follows as `0x03 AUDIO` binary frames. */
  z.object({
    t: z.literal("audio"),
    codec: z.number().int(),
    sampleRate: z.number().int().positive(),
    channels: z.number().int().positive(),
    frameMs: z.number().int().positive(),
  }),
  // server-run mode (§17.2.1)
  z.object({ t: z.literal("frame"), frame: z.number().int(), pixels: z.string() /* base64, own screen only */ }),
]);
export type LinkMessage = z.infer<typeof LinkMessage>;

/**
 * Binary WebSocket frames (§20.5 "binary, 1-byte type prefix"). Server → client only; a client that
 * sends binary is disconnected.
 *
 *   0x01 STATE  u8 side (0 = A, 1 = B) | opponent's verified core state, zstd as stored
 *   0x03 AUDIO  u8 codec (`LinkAudioCodec`) | u32le seq | codec packet
 */
export const LinkBinOp = { STATE: 0x01, AUDIO: 0x03 } as const;
export type LinkBinOp = (typeof LinkBinOp)[keyof typeof LinkBinOp];

/** codec 1 = μ-law 16 kHz mono 20 ms; codec 2 = Opus 48 kHz mono 20 ms, 32 kbps. */
export const LinkAudioCodec = { MULAW_16K: 1, OPUS_48K: 2 } as const;
export const LINK_AUDIO_FORMATS: Record<number, { sampleRate: number; channels: number; frameMs: number }> = {
  [LinkAudioCodec.MULAW_16K]: { sampleRate: 16_000, channels: 1, frameMs: 20 },
  [LinkAudioCodec.OPUS_48K]: { sampleRate: 48_000, channels: 1, frameMs: 20 },
};

export function encodeLinkState(side: 0 | 1, zstdState: Uint8Array): Uint8Array {
  const out = new Uint8Array(2 + zstdState.length);
  out[0] = LinkBinOp.STATE;
  out[1] = side;
  out.set(zstdState, 2);
  return out;
}

export function encodeLinkAudio(codec: number, seq: number, packet: Uint8Array): Uint8Array {
  const out = new Uint8Array(6 + packet.length);
  out[0] = LinkBinOp.AUDIO;
  out[1] = codec;
  new DataView(out.buffer).setUint32(2, seq >>> 0, true);
  out.set(packet, 6);
  return out;
}

export type LinkBinaryFrame =
  | { op: typeof LinkBinOp.STATE; side: "A" | "B"; zstdState: Uint8Array }
  | { op: typeof LinkBinOp.AUDIO; codec: number; seq: number; packet: Uint8Array };

/** Parses a binary frame; null for unknown opcodes or truncated frames. */
export function decodeLinkBinary(b: Uint8Array): LinkBinaryFrame | null {
  if (b.length < 2) return null;
  if (b[0] === LinkBinOp.STATE) {
    if (b[1] !== 0 && b[1] !== 1) return null;
    return { op: LinkBinOp.STATE, side: b[1] === 0 ? "A" : "B", zstdState: b.subarray(2) };
  }
  if (b[0] === LinkBinOp.AUDIO) {
    if (b.length < 6) return null;
    const seq = new DataView(b.buffer, b.byteOffset, b.byteLength).getUint32(2, true);
    return { op: LinkBinOp.AUDIO, codec: b[1]!, seq, packet: b.subarray(6) };
  }
  return null;
}
